const ORIGIN = (
  process.env.REACT_APP_DOCTOR_PORTAL_ORIGIN || "https://doctor.scribeinai.com"
).replace(/\/+$/, "");

export const SITE = {
  origin: ORIGIN,
  name: "ScribeInAI Doctor Dashboard",
  shortName: "Doctor Portal",
  brand: "ScribeInAI",
  locale: "en_US",
  language: "en",
  themeColor: "#0c2139",
  imagePath: "/doctor-login-hero.png",
  logoPath: "/image.png",
  imageAlt: "ScribeInAI doctor dashboard — medical dictation, transcription tracking, and clinical documents",
  sameAs: ["https://scribeinai.com"],
  keywords: [
    "doctor dictation dashboard",
    "medical transcription portal",
    "physician dictation software",
    "AI medical scribe",
    "clinical transcription dashboard",
    "healthcare provider portal",
    "HIPAA conscious dictation",
    "ScribeInAI doctor",
    "medical document transcription",
  ].join(", "),
};

export const DEFAULT_DESCRIPTION =
  "ScribeInAI Doctor Dashboard — secure medical dictation for physicians. Record encounters, track transcription jobs, manage schedules and templates, and download completed clinical documents.";

export const PAGES = {
  home: {
    title: "Doctor Dictation Dashboard | ScribeInAI",
    description: DEFAULT_DESCRIPTION,
    canonicalPath: "/",
    path: "/",
  },
  login: {
    title: "Sign In · Doctor Dashboard | ScribeInAI",
    description:
      "Sign in to the ScribeInAI Doctor Dashboard to dictate clinical encounters, track transcription jobs, and download finished medical documents.",
    canonicalPath: "/",
    path: "/login",
  },
  register: {
    title: "Create Provider Account · Doctor Dashboard | ScribeInAI",
    description:
      "Register for the ScribeInAI Doctor Dashboard. Create a provider profile so an administrator can verify access to medical dictation and transcription.",
    canonicalPath: "/register",
    path: "/register",
  },
  dashboard: {
    title: "Doctor Dashboard · Dictate, Analytics & Documents | ScribeInAI",
    description:
      "Open your ScribeInAI Doctor Dashboard: analytics, dictation, pending files, completed documents, schedules, and templates for healthcare providers.",
    canonicalPath: "/doctor-dashboard",
    path: "/doctor-dashboard",
  },
  profile: {
    title: "Profile · Doctor Dashboard | ScribeInAI",
    description: "Manage your ScribeInAI Doctor Dashboard profile and account settings.",
    canonicalPath: "/profile",
    path: "/profile",
    noindex: true,
  },
};

export function absoluteUrl(path = "/") {
  const p = path.startsWith("/") ? path : `/${path}`;
  return p === "/" ? `${SITE.origin}/` : `${SITE.origin}${p}`;
}

export function ogImageUrl() {
  return absoluteUrl(SITE.imagePath);
}

export function logoUrl() {
  return absoluteUrl(SITE.logoPath);
}

export function buildGraphLd({ title, description, canonical, pagePath }) {
  const orgId = `${SITE.origin}/#organization`;
  const websiteId = `${SITE.origin}/#website`;
  const appId = `${SITE.origin}/#app`;
  const webpageId = `${canonical}#webpage`;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": orgId,
        name: SITE.brand,
        url: "https://scribeinai.com",
        logo: {
          "@type": "ImageObject",
          url: logoUrl(),
        },
        sameAs: SITE.sameAs,
      },
      {
        "@type": "WebSite",
        "@id": websiteId,
        name: SITE.name,
        url: `${SITE.origin}/`,
        inLanguage: SITE.language,
        publisher: { "@id": orgId },
      },
      {
        "@type": "SoftwareApplication",
        "@id": appId,
        name: SITE.name,
        applicationCategory: "HealthApplication",
        applicationSubCategory: "Medical dictation",
        operatingSystem: "Web",
        url: `${SITE.origin}/`,
        image: ogImageUrl(),
        description: DEFAULT_DESCRIPTION,
        featureList: [
          "Clinical dictation",
          "AI medical transcription",
          "Job analytics",
          "Pending file tracking",
          "Completed document download",
          "Schedule management",
          "Template management",
        ],
        publisher: { "@id": orgId },
      },
      {
        "@type": "WebPage",
        "@id": webpageId,
        url: canonical,
        name: title,
        description,
        inLanguage: SITE.language,
        isPartOf: { "@id": websiteId },
        about: { "@id": appId },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: ogImageUrl(),
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: SITE.brand,
            item: "https://scribeinai.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: SITE.shortName,
            item: `${SITE.origin}/`,
          },
          ...(pagePath && pagePath !== "/"
            ? [
                {
                  "@type": "ListItem",
                  position: 3,
                  name: title,
                  item: canonical,
                },
              ]
            : []),
        ],
      },
    ],
  };
}
